import {
  createContext,
  useEffect,
  useMemo,
  useState,
  useContext,
  useRef
} from 'react';
import isEqual from 'lodash/isEqual';
import { useSoyuzLocales, useSoyuzExperiment } from '@spotahome/soyuz/client';
import { FilterUtils, useFiltersContext } from '@spotahome/ui-library';
import { getResultsPerPageFromBehaviour } from '@spotahome/ui-library/src/utils/searchResults';

import debounce from '@spotahome/ui-library/src/tenant/components/utils/debounce';
import getGqlClient from '@spotahome/ui-library/src/tenant/clients/graphql/marketplace';
import CardPaginator from '@spotahome/marketplace-common/src/lib/card-paginator';

import { useHighlightMarkersContext } from '@spotahome/marketplace-common/src/context/HighlightMarkersContext';

import {
  getMarkersQuery,
  getSeoSearchResults
} from '../common/graphql/queries/nMarkers';
import getHomecardsQuery from '../common/graphql/queries/homecards';

const SeoSearchContext = createContext({});

const WAIT_TO_REQUEST = 150;

const SeoSearchProvider = ({
  children,
  initialHomecards,
  initialFilters,
  initialNMarkers,
  cityId
}) => {
  const cardPaginator = useRef(null);
  const prevFilters = useRef(initialFilters);
  const { current: currentLocale } = useSoyuzLocales();
  const { filters, handleUpdatePage } = useFiltersContext();
  const [isLoadingHomecards, setIsLoadingHomecards] = useState(false);
  const [homecards, setHomecards] = useState(initialHomecards);
  const [totalResults, setTotalResults] = useState(initialNMarkers);
  const [locationCoordinates, setLocationCoordinates] = useState(null);
  const [mapBoundariesFilter, setMapBoundariesFilter] = useState(null);

  const { markerClickedId } = useHighlightMarkersContext();

  const reduceSearchResultsPerPageV2Experiment = useSoyuzExperiment(
    'ReduceSearchResultsPerPageV2'
  );
  const pageSize = getResultsPerPageFromBehaviour(
    reduceSearchResultsPerPageV2Experiment.get('behaviour')
  );

  const requestFullSearch = async filtersToRequest => {
    const {
      totalMarkers,
      homecards: newHomecards,
      markers: newMarkers
    } = await getSeoSearchResults(getGqlClient())(
      cityId,
      currentLocale,
      FilterUtils.adaptFiltersFromUrl(filtersToRequest),
      pageSize
    );

    setTotalResults(totalMarkers);
    cardPaginator.current.updateData({
      numberOfMarkers: totalMarkers,
      data: newMarkers
    });
    setHomecards(newHomecards);
    setIsLoadingHomecards(false);
  };

  const debouncedGetSearch = useMemo(
    () => debounce(requestFullSearch, WAIT_TO_REQUEST),
    []
  );

  const requestOnlyHomecards = async () => {
    const currentPagedMarkers = cardPaginator.current.getCurrentPageContent();

    const { data } = await getHomecardsQuery(getGqlClient())(
      currentPagedMarkers.map(marker => parseInt(marker.id, 10)),
      currentLocale
    );

    setHomecards(data.homecards);
    setIsLoadingHomecards(false);
  };

  const debouncedGetHomecards = useMemo(
    () => debounce(requestOnlyHomecards, WAIT_TO_REQUEST),
    []
  );

  useEffect(() => {
    const { page: prevPage, ...restPrevFilters } = prevFilters.current;
    const { page, ...restFilters } = filters;

    if (!isEqual(restFilters, restPrevFilters)) {
      setIsLoadingHomecards(true);
      debouncedGetSearch(filters);
    } else if (page !== prevPage) {
      setIsLoadingHomecards(true);
      debouncedGetHomecards();
    }

    prevFilters.current = filters;
  }, [filters]);

  useEffect(() => {
    const requestMarkers = async filtersToRequest => {
      const { search, geocodeLocation } = await getMarkersQuery(getGqlClient())(
        cityId,
        FilterUtils.adaptFiltersFromUrl(filtersToRequest)
      );
      const { markers } = search;
      cardPaginator.current = new CardPaginator({
        initialPage: filters.page,
        pageSize,
        numberOfMarkers: markers.length
      });
      cardPaginator.current.initializeData(markers);
      setLocationCoordinates(geocodeLocation);
    };

    requestMarkers(initialFilters);
  }, []);

  useEffect(() => {
    if (mapBoundariesFilter && !markerClickedId) {
      cardPaginator.current.filter(marker =>
        mapBoundariesFilter({
          lat: marker.coord[1],
          lng: marker.coord[0]
        })
      );
      setIsLoadingHomecards(true);
      requestOnlyHomecards();
    }
  }, [mapBoundariesFilter, markerClickedId]);

  return (
    <SeoSearchContext.Provider
      value={{
        isLoadingHomecards,
        homecards,
        totalResults,
        currentMarkers: cardPaginator.current?.getCurrentPageContent() || [],
        paginator: {
          isPrevButtonDisabled:
            cardPaginator.current?.getPreviousPageNumber() === null,
          isNextButtonDisabled:
            cardPaginator.current?.getNextPageNumber() === null,
          handleNextPage: () => {
            cardPaginator.current?.nextPage();
            handleUpdatePage(cardPaginator.current?.getCurrentPageNumber());
          },
          handlePrevPage: () => {
            cardPaginator.current?.previousPage();
            handleUpdatePage(cardPaginator.current?.getCurrentPageNumber());
          },
          getCurrentPageNumber: () =>
            cardPaginator.current?.getCurrentPageNumber(),
          getTotalPages: () => cardPaginator.current?.getTotalPages(),
          getPreviousPageNumber: () =>
            cardPaginator.current?.getPreviousPageNumber(),
          getNextPageNumber: () => cardPaginator.current?.getNextPageNumber(),
          setCurrentPage: page => {
            cardPaginator.current?.setCurrentPage(page);
            handleUpdatePage(cardPaginator.current?.getCurrentPageNumber());
          }
        },
        locationCoordinates,
        cityId,
        setMapBoundariesFilter: newFilter => {
          setMapBoundariesFilter(() => newFilter); // To avoid React for interpreting the function as a "prevProp" function
        }
      }}
    >
      {children}
    </SeoSearchContext.Provider>
  );
};

export const useSeoSearchContext = () => useContext(SeoSearchContext);

export const useSeoSearchMarkers = () => {
  const { currentMarkers } = useContext(SeoSearchContext);

  return currentMarkers;
};

export const useSeoSearchHomecards = () => {
  const { homecards } = useContext(SeoSearchContext);

  return homecards;
};

export const useSeoTotalSearchResults = () => {
  const { totalResults } = useContext(SeoSearchContext);

  return totalResults;
};

export const useSeoSearchPaginator = () => {
  const { paginator } = useContext(SeoSearchContext);

  return paginator;
};

export default SeoSearchProvider;
